// Foco — tablero de pendientes que administra el agente.
// Columnas por estado, arrastrar y soltar nativo (como deals), captura rápida,
// filtro por persona y área, y el panel «Hoy» que calcula el backend (no aquí).
// Tokens semánticos del tema (bg-card, text-foreground…), nunca colores fijos.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Calendar, CheckCircle2, Clock, Hourglass, Lock, Plus, RefreshCw, Target, X } from 'lucide-react';

import * as api from '../api';
import type { FocusItem, FocusMember, FocusStatus, FocusToday } from '../types';

const COLUMNS: { key: FocusStatus; label: string; hint: string }[] = [
  { key: 'inbox', label: 'Por ordenar', hint: 'Lo que se capturó y aún no tiene lugar' },
  { key: 'next', label: 'Siguiente', hint: 'Lo que va después' },
  { key: 'today', label: 'Hoy', hint: 'Una cosa a la vez' },
  { key: 'waiting', label: 'Esperando a alguien', hint: 'Depende de otra persona' },
  { key: 'done', label: 'Hecho', hint: 'Últimos 7 días' },
];

const fmtDate = (iso: string | null) =>
  iso ? new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short' }).format(new Date(`${iso}T12:00:00`)) : '';

function Card({ item, onDragStart, onDone }: { item: FocusItem; onDragStart: () => void; onDone: () => void }) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      className="group rounded-xl border border-border bg-card p-3 shadow-sm hover:shadow-md transition cursor-grab active:cursor-grabbing"
    >
      <div className="flex items-start gap-2">
        <span className="text-[11px] font-mono text-muted-foreground mt-0.5">#{item.numero}</span>
        <p className="flex-1 text-sm font-medium text-foreground leading-snug">{item.titulo}</p>
        {item.estado !== 'done' && (
          <button
            type="button"
            onClick={onDone}
            aria-label={`Marcar #${item.numero} como hecho`}
            className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-muted-foreground hover:text-primary transition"
          >
            <CheckCircle2 className="w-4 h-4" />
          </button>
        )}
      </div>
      {item.siguiente_paso && item.estado !== 'done' && (
        <p className="mt-1.5 text-xs text-muted-foreground">→ {item.siguiente_paso}</p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
        <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{item.responsable.split(' ')[0]}</span>
        {item.area && <span className="rounded-full border border-border px-2 py-0.5 text-muted-foreground">{item.area}</span>}
        {item.fecha_limite && (
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${item.vencido ? 'bg-destructive/10 text-destructive' : 'text-muted-foreground'}`}>
            <Calendar className="w-3 h-3" /> {fmtDate(item.fecha_limite)}
          </span>
        )}
        {item.prioridad === 1 && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">Alta</span>}
        {item.atorado && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="w-3 h-3" /> Atorado
          </span>
        )}
        {item.esperando_a && (
          <span className="inline-flex items-center gap-1 text-muted-foreground"><Hourglass className="w-3 h-3" /> {item.esperando_a}</span>
        )}
        {item.privado && <Lock className="w-3 h-3 text-muted-foreground" aria-label="Privado" />}
      </div>
    </div>
  );
}

export function FocoBoard() {
  const [items, setItems] = useState<FocusItem[]>([]);
  const [team, setTeam] = useState<FocusMember[]>([]);
  const [persona, setPersona] = useState('');
  const [area, setArea] = useState('');
  const [hoy, setHoy] = useState<FocusToday | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState<FocusItem | null>(null);
  const [over, setOver] = useState<FocusStatus | null>(null);
  const [draft, setDraft] = useState('');
  const [draftOwner, setDraftOwner] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [l, t, h] = await Promise.all([api.listItems(persona), api.listTeam(), api.today(persona).catch(() => null)]);
      setItems(l.pendientes);
      setTeam(t.equipo);
      setHoy(h);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el tablero');
    } finally {
      setLoading(false);
    }
  }, [persona]);

  useEffect(() => {
    void load();
  }, [load]);

  const areas = useMemo(() => Array.from(new Set(items.map((i) => i.area).filter(Boolean))).sort(), [items]);
  const weekAgo = Date.now() - 7 * 86400000;
  const byCol = useMemo(() => {
    const m: Record<string, FocusItem[]> = {};
    for (const c of COLUMNS) m[c.key] = [];
    for (const i of items) {
      if (area && i.area !== area) continue;
      if (i.estado === 'dropped') continue;
      if (i.estado === 'done' && i.hecho && new Date(i.hecho).getTime() < weekAgo) continue;
      m[i.estado]?.push(i);
    }
    for (const k of Object.keys(m)) m[k].sort((a, b) => a.posicion - b.posicion || a.prioridad - b.prioridad || a.numero - b.numero);
    return m;
  }, [items, area, weekAgo]);

  async function drop(col: FocusStatus) {
    const it = dragging;
    setDragging(null);
    setOver(null);
    if (!it || it.estado === col) return;
    setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, estado: col } : x)));
    try {
      if (col === 'done') await api.closeItem(it.numero);
      else await api.moveItem(it.numero, col);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo mover');
    }
    void load();
  }

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const t = draft.trim();
    if (!t) return;
    setDraft('');
    try {
      await api.createItem({ titulo: t, ...(draftOwner ? { responsable: draftOwner } : {}) });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear');
    }
    void load();
  }

  return (
    <div className="p-4 md:p-6 space-y-5">
      <header className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-primary" />
          <h1 className="text-xl font-semibold text-foreground">Foco</h1>
        </div>
        <p className="text-sm text-muted-foreground hidden md:block">
          Tus pendientes los lleva el agente: escríbele por WhatsApp «anota que…», «listo el 12» o «pásalo a mañana».
        </p>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <select value={persona} onChange={(e) => setPersona(e.target.value)} className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground" aria-label="Persona">
            <option value="">Todo el equipo</option>
            {team.map((m) => <option key={m.usuario} value={m.usuario}>{m.nombre}</option>)}
          </select>
          {areas.length > 0 && (
            <select value={area} onChange={(e) => setArea(e.target.value)} className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground" aria-label="Área">
              <option value="">Todas las áreas</option>
              {areas.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          )}
          <button type="button" onClick={() => void load()} className="rounded-lg border border-border p-2 text-muted-foreground hover:text-foreground" aria-label="Recargar">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </header>

      {error && (
        <div role="alert" className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
          <button type="button" onClick={() => setError('')} className="ml-auto" aria-label="Cerrar"><X className="w-4 h-4" /></button>
        </div>
      )}

      {hoy && (
        <section className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <Clock className="w-4 h-4 text-primary" />
            <h2 className="font-semibold text-foreground">Hoy, {hoy.persona.split(' ')[0]}</h2>
            <span className="text-xs text-muted-foreground">
              {hoy.abiertos_total} abiertos · {hoy.vencidos} vencidos · {hoy.esperando_a_alguien} esperando a alguien
            </span>
          </div>
          {hoy.hoy.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nada pendiente para hoy.</p>
          ) : (
            <ol className="grid gap-2 md:grid-cols-3">
              {hoy.hoy.map((i, n) => (
                <li key={i.id} className="rounded-xl bg-card border border-border p-3 text-sm">
                  <span className="text-primary font-semibold mr-1">{n + 1}.</span>
                  <span className="text-foreground">{i.titulo}</span>
                  <span className="ml-1 text-[11px] font-mono text-muted-foreground">#{i.numero}</span>
                  {i.siguiente_paso && <p className="mt-1 text-xs text-muted-foreground">→ {i.siguiente_paso}</p>}
                </li>
              ))}
            </ol>
          )}
        </section>
      )}

      <form onSubmit={add} className="flex flex-wrap gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Anota un pendiente (empieza con verbo: «Mandar propuesta a…»)"
          className="flex-1 min-w-[220px] rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
        />
        {team.length > 1 && (
          <select value={draftOwner} onChange={(e) => setDraftOwner(e.target.value)} className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground" aria-label="Responsable">
            <option value="">Para mí</option>
            {team.map((m) => <option key={m.usuario} value={m.usuario}>{m.nombre}</option>)}
          </select>
        )}
        <button type="submit" className="inline-flex items-center gap-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
          <Plus className="w-4 h-4" /> Anotar
        </button>
      </form>

      <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-5">
        {COLUMNS.map((c) => (
          <section
            key={c.key}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(c.key);
            }}
            onDragLeave={() => setOver(null)}
            onDrop={() => void drop(c.key)}
            className={`rounded-2xl border p-3 min-h-[160px] transition ${over === c.key ? 'border-primary bg-primary/5' : 'border-border bg-muted/30'}`}
            aria-label={c.label}
          >
            <header className="mb-2 flex items-baseline justify-between">
              <h3 className="text-sm font-semibold text-foreground">{c.label}</h3>
              <span className="text-xs text-muted-foreground">{byCol[c.key].length}</span>
            </header>
            <p className="mb-3 text-[11px] text-muted-foreground">{c.hint}</p>
            <div className="space-y-2">
              {byCol[c.key].map((i) => (
                <Card key={i.id} item={i} onDragStart={() => setDragging(i)} onDone={() => void api.closeItem(i.numero).then(load)} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
